import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { inr, pct } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, UploadCloud } from "lucide-react";
import { formatApiError } from "@/lib/api";
import SKUUploadDialog from "@/components/SKUUploadDialog";

const EMPTY = { sku_code: "", product_name: "", category: "", mrp: 0, cogs: 0, packaging_cost: 0, gst_percent: 18, weight_grams: 0 };

export default function SKUs() {
  const { current } = useWorkspace();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    if (!current) return;
    setLoading(true);
    try {
      const { data } = await api.get("/skus", { params: { workspace_id: current.id } });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [current]);

  const startAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (s) => { setEditing(s); setForm({ ...s }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      sku_code: form.sku_code, product_name: form.product_name, category: form.category,
      mrp: Number(form.mrp), cogs: Number(form.cogs), packaging_cost: Number(form.packaging_cost),
      gst_percent: Number(form.gst_percent),
      weight_grams: form.weight_grams ? Number(form.weight_grams) : null,
    };
    try {
      if (editing) {
        await api.put(`/skus/${editing.id}`, payload);
        toast.success("SKU updated");
      } else {
        await api.post("/skus", payload, { params: { workspace_id: current.id } });
        toast.success("SKU created");
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.sku_code}?`)) return;
    await api.delete(`/skus/${s.id}`);
    toast.success("SKU deleted");
    load();
  };

  const totalUnitCost = Number(form.cogs || 0) + Number(form.packaging_cost || 0);

  if (!current) return <div className="p-12 text-muted-foreground">No workspace selected.</div>;

  return (
    <div className="min-h-screen">
      <div className="border-b border-border p-8 flex items-end justify-between">
        <div>
          <div className="overline text-muted-foreground">{current.brand_name}</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">SKU Database</h1>
          <p className="text-sm text-muted-foreground mt-2">{items.length} products · unit cost auto-calculated</p>
        </div>
        <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => setUploadOpen(true)} className="rounded-none" data-testid="upload-sku-csv-btn">
          <UploadCloud className="h-4 w-4 mr-1" /> Upload CSV
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={startAdd} className="rounded-none" data-testid="add-sku-btn"><Plus className="h-4 w-4 mr-1" /> Add SKU</Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-lg">
            <DialogHeader><DialogTitle className="font-display">{editing ? "Edit SKU" : "New SKU"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="overline">SKU Code</Label><Input required value={form.sku_code} onChange={(e) => setForm({ ...form, sku_code: e.target.value })} className="rounded-none mt-2" data-testid="sku-code" /></div>
                <div><Label className="overline">Category</Label><Input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-none mt-2" data-testid="sku-category" /></div>
              </div>
              <div><Label className="overline">Product Name</Label><Input required value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="rounded-none mt-2" data-testid="sku-name" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="overline">MRP (₹)</Label><Input type="number" step="0.01" required value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className="rounded-none mt-2 font-mono" data-testid="sku-mrp" /></div>
                <div><Label className="overline">GST %</Label><Input type="number" step="0.01" required value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} className="rounded-none mt-2 font-mono" data-testid="sku-gst" /></div>
                <div><Label className="overline">COGS (₹)</Label><Input type="number" step="0.01" required value={form.cogs} onChange={(e) => setForm({ ...form, cogs: e.target.value })} className="rounded-none mt-2 font-mono" data-testid="sku-cogs" /></div>
                <div><Label className="overline">Packaging (₹)</Label><Input type="number" step="0.01" required value={form.packaging_cost} onChange={(e) => setForm({ ...form, packaging_cost: e.target.value })} className="rounded-none mt-2 font-mono" data-testid="sku-pack" /></div>
                <div><Label className="overline">Weight (grams)</Label><Input type="number" step="1" placeholder="Optional" value={form.weight_grams || ""} onChange={(e) => setForm({ ...form, weight_grams: e.target.value })} className="rounded-none mt-2 font-mono" data-testid="sku-weight" /></div>
              </div>
              <div className="border border-dashed border-border p-3 flex items-center justify-between">
                <span className="overline text-muted-foreground">Total Unit Cost</span>
                <span className="font-mono text-lg font-semibold" data-testid="sku-total-cost">{inr(totalUnitCost, { decimals: 2 })}</span>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
                <Button type="submit" className="rounded-none" data-testid="sku-save">{editing ? "Save" : "Create"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <SKUUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onCommitted={load} />

      <div className="p-8">
        <div className="border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="overline">SKU</TableHead>
                <TableHead className="overline">Product</TableHead>
                <TableHead className="overline">Category</TableHead>
                <TableHead className="overline text-right">MRP</TableHead>
                <TableHead className="overline text-right">COGS</TableHead>
                <TableHead className="overline text-right">Packaging</TableHead>
                <TableHead className="overline text-right">GST</TableHead>
                <TableHead className="overline text-right">Unit Cost</TableHead>
                <TableHead className="overline text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="sku-table">
              {loading && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>}
              {!loading && items.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No SKUs yet.</TableCell></TableRow>}
              {items.map((s) => (
                <TableRow key={s.id} data-testid={`sku-row-${s.sku_code}`}>
                  <TableCell className="font-mono text-xs">{s.sku_code}</TableCell>
                  <TableCell className="font-medium">{s.product_name}</TableCell>
                  <TableCell className="text-muted-foreground">{s.category}</TableCell>
                  <TableCell className="text-right font-mono">{inr(s.mrp, { decimals: 2 })}</TableCell>
                  <TableCell className="text-right font-mono">{inr(s.cogs, { decimals: 2 })}</TableCell>
                  <TableCell className="text-right font-mono">{inr(s.packaging_cost, { decimals: 2 })}</TableCell>
                  <TableCell className="text-right font-mono">{pct(s.gst_percent, 0)}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{inr(s.total_unit_cost, { decimals: 2 })}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => startEdit(s)} data-testid={`edit-${s.sku_code}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`del-${s.sku_code}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
